import { useCallback, useMemo, useState } from 'react';
import { useLang } from 'hooks/lang';

import { actions, RepriceControl, projectStatusConfig, getServiceName } from 'entities';

import { Button, Column } from 'components/Blocks';
import { TextareaWithFiles } from 'components/Inputs';

import { CircleDollarIcon } from 'components/Icons';

import { FullyInvoicedRepriceAlert } from 'pages/Invoice/components';

import { ReasonSelect } from './ReasonSelect';
import { ServiceSelect } from './ServiceSelect';
import { CountrySelect } from './CountrySelect';
import { SelectedReason } from './SelectedReason';
import { InvoiceOriginNotice } from './InvoiceOriginText';

import { useProjectProfile } from '../../../context';
import { useFileInput, useUser } from 'hooks';

import { listGet, sortByProperty } from 'utils/array';

export function RequestRepriceForm() {
  const { t } = useLang();
  const user = useUser();

  const fromInvoice = !!user.team.isInvoiceTeam;

  const { project, updateProject } = useProjectProfile();

  const { services } = project;

  const [control, setControl] = useState<RepriceControl>({});

  const [comment, setComment] = useState('');
  const [reason, setReason] = useState<string | null>(null);
  const { files, ...fileInputProps } = useFileInput();

  const canSubmit = fromInvoice
    ? !!comment.trim()
    : Object.keys(control).length &&
      reason &&
      Object.values(control).every((countryControl) => {
        const countryJustifications = Object.values(countryControl);

        return countryJustifications.every(Boolean) && countryJustifications.length;
      });

  const requestReprice = actions.projects.requestProjectReprice.use({
    block: !canSubmit,

    params: {
      justifications: fromInvoice ? undefined : control,
      comment,
      project: project.id,
      reason: reason!,
      files: files.map((f) => f.file),
    },

    onSuccess: () => {
      updateProject((old) => ({
        ...old,
        reprice: 'REQUESTED',
        repriceOrigin: fromInvoice ? 'INVOICE' : 'EXECUTION',
        status: projectStatusConfig.pendingApproval.code,
      }));
    },
  });

  const handleServiceClick = useCallback((services: string[]) => {
    setControl((old) => {
      return Object.fromEntries(services.map((id) => [id, old[id] ?? {}]));
    });
  }, []);

  const handleCountryClick = useCallback((serviceId: string, country: string) => {
    setControl((old) => {
      const serviceControl = { ...old[serviceId] };

      const countryWasSelected = country in serviceControl;

      if (!countryWasSelected) return { ...old, [serviceId]: { ...serviceControl, [country]: '' } };

      delete serviceControl[country];

      return { ...old, [serviceId]: serviceControl };
    });
  }, []);

  const onJustification = useCallback((serviceId: string, country: string, value: string) => {
    setControl((old) => ({
      ...old,
      [serviceId]: {
        ...old[serviceId],
        [country]: value,
      },
    }));
  }, []);

  const refServices = useMemo(() => {
    const withTask = project.tasks.map((task) => {
      const service = listGet(services, task.serviceConfigId)!;

      return {
        ...service,
        taskName: task.name,
        taskId: task.id,
        taskStatus: task.status,
        label: getServiceName(task.serviceConfigId, task),
        disabled: !!service.outsourceConfig && !service.outsourceConfig.approved,
      };
    });

    const list = sortByProperty(withTask, 'label');

    return {
      list,
      byId: Object.fromEntries(list.map((s) => [s.id, s])),
    };
  }, [project.tasks, services]);

  const servicesSelected = Object.keys(control);

  return (
    <>
      {reason ? (
        <Column>
          <SelectedReason reason={reason} setReason={setReason} />

          {fromInvoice ? (
            <InvoiceOriginNotice />
          ) : (
            <>
              <ServiceSelect
                selectedServices={servicesSelected}
                onServiceChange={handleServiceClick}
                services={refServices.list}
                currency={project.currency}
              />

              <CountrySelect
                repriceControl={control}
                selectedServices={servicesSelected}
                serviceMap={refServices.byId}
                onJustification={onJustification}
                handleCountryClick={handleCountryClick}
              />
            </>
          )}

          <TextareaWithFiles
            value={comment}
            onChange={({ target }) => setComment(target.value)}
            label={t('add-comments')}
            files={files}
            {...fileInputProps}
          />
        </Column>
      ) : (
        <ReasonSelect reason={reason} setReason={setReason} />
      )}

      <FullyInvoicedRepriceAlert invoice={project.invoice} />

      <Button {...requestReprice.buttonProps} disabled={!reason || !canSubmit}>
        <CircleDollarIcon />
        {reason ? t('request-reprice') : t('continue')}
      </Button>
    </>
  );
}
